import React, { useCallback, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import lang from '../../lib/language';
import { HighGrowthCompanyData } from '../../lib/models/fundamental/types';
import { RootState } from '../../lib/store';

import { useIsMobile } from '../../hooks';

import Chart from './Chart/Chart';
import CompanyInfo from './CompanyInfo/CompanyInfo';
import CompanyInfoMobile from './CompanyInfo/CompanyInfoMobile';
import HighGrowthCompaniesInfo from './HighGrowthCompaniesInfo/HighGrowthCompaniesInfo';

import styles from './DividendPyersAndHighGrowthCompanies.module.scss';

type OwnProps = {
  companiesContent: HighGrowthCompanyData[]
}
type Props = OwnProps & ReduxProps;

const HighGrowthCompanies = ({ companiesContent, companyLogoAndName }: Props) => {
  const MOBILE_COMPANIES_COUNT_LIMIT = 6;
  const isMobile = useIsMobile();
  const [company, setCompany] = useState<HighGrowthCompanyData>(companiesContent[0]);
  const onChange = useCallback((key) => {
    const filteredDividends = companiesContent.find(el => el.symbol === key);
    if (filteredDividends) setCompany(filteredDividends);
  }, [companiesContent]);

  return (
    <>
      { isMobile ? (
        // TODO: Remove div style after applying correct widths - HW-506
        <div className={styles.wrapper} style={{ minWidth: (window.innerWidth / 100) * 90 }}>
          <div className={styles.chartDetails}>
            <p className={styles.dotWrapper}>
              <span className={styles.activeDot} />
              {lang.commonDiscoverHighGrowthCompaniesLegend()}
              <HighGrowthCompaniesInfo />
            </p>
            <Chart
              data={isMobile ? companiesContent.slice(0, MOBILE_COMPANIES_COUNT_LIMIT) : companiesContent}
              yAxis={'growthIndicator'}
              currentItemId={companiesContent[0] ? companiesContent[0].symbol : ''}
              onItemChange={onChange}
              isHighGrowthCompany
            />
          </div>
          <div className={styles.line} />
          { company ? (
            <CompanyInfoMobile
              symbol={company.symbol}
              logo={companyLogoAndName[company.symbol].logo}
              url={companyLogoAndName[company.symbol].url ?? company.url}
              companyName={companyLogoAndName[company.symbol].name}
              areDividends={false}
              volume={company.avg30Volume}
              marketCap={company.marketCap}
              prevCloseAndEPS={company.epsLast12Months}
              dividendRateAndRevenue={company.revenue}
            />
          ) : (
            <CompanyInfoMobile
              isLoading
            />
          )}
        </div>
      ) : (
        <div className={styles.wrapper}>
          { company ? (
            <CompanyInfo
              symbol={company.symbol}
              logo={companyLogoAndName[company.symbol].logo}
              url={companyLogoAndName[company.symbol].url ?? company.url}
              companyName={companyLogoAndName[company.symbol].name}
              areDividends={false}
              volume={company.avg30Volume}
              marketCap={company.marketCap}
              prevCloseAndEPS={company.epsLast12Months}
              dividendRateAndRevenue={company.revenue}
            />
          ) : (
            <CompanyInfo
              isLoading
            />
          )}
          <div className={styles.chartDetails}>
            <p className={styles.dotWrapper}>
              <span className={styles.activeDot} />
              {lang.commonDiscoverHighGrowthCompaniesLegend()}
              <HighGrowthCompaniesInfo />
            </p>
            <Chart
              data={companiesContent}
              yAxis={'growthIndicator'}
              currentItemId={company ? company.symbol : ''}
              onItemChange={onChange}
              isHighGrowthCompany
            />
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyLogoAndName: state.fundamental.companyLogoAndName,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(HighGrowthCompanies);
